<template>
  <div>
    <div class="flex items-baseline justify-between mb-6">
      <div>
        <h1 class="text-[26px] font-bold tracking-tight text-ink m-0 max-md:text-[22px]">{{ greeting }}</h1>
        <div class="flex items-center gap-2 mt-1 text-[13px] font-medium">
          <span class="text-muted">{{ today }}</span>
          <template v-if="overdueCases.length || upcomingDeadlines.length">
            <span class="text-placeholder">&middot;</span>
            <span v-if="overdueCases.length" class="text-[#e53935]">{{ overdueCases.length }} overdue</span>
            <span v-if="overdueCases.length && upcomingDeadlines.length" class="text-placeholder">&middot;</span>
            <span v-if="upcomingDeadlines.length" class="text-[#f57c00]">{{ upcomingDeadlines.length }} due soon</span>
          </template>
        </div>
      </div>
    </div>

    <skeleton-overview v-if="isLoading"></skeleton-overview>

    <template v-else>
      <!-- Stats strip -->
      <div class="flex bg-white border border-black/6 rounded-[20px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-6 max-[1100px]:flex-wrap max-md:flex-wrap max-md:rounded-[16px]">
        <router-link
          :to="{ name: 'admin-applicants' }"
          class="stat-cell flex-1 flex flex-col gap-1 py-5 px-7 no-underline text-inherit transition-colors duration-150 hover:bg-black/[0.025] max-[1100px]:basis-[40%] max-[1100px]:grow max-md:basis-[40%] max-md:grow max-md:py-4 max-md:px-4">
          <span class="text-[28px] font-bold text-ink leading-tight tracking-tight max-md:text-[22px]">{{ activeApplicants.length }}</span>
          <span class="text-xs text-muted font-medium">Active cases</span>
        </router-link>
        <router-link
          :to="{ name: 'admin-applicants', query: { sort: 'progress', dir: 'desc' } }"
          class="stat-cell flex-1 flex flex-col gap-1 py-5 px-7 no-underline text-inherit transition-colors duration-150 hover:bg-black/[0.025] max-[1100px]:basis-[40%] max-[1100px]:grow max-md:basis-[40%] max-md:grow max-md:py-4 max-md:px-4">
          <span class="text-[28px] font-bold text-[#2d7a0e] leading-tight tracking-tight max-md:text-[22px]">{{ completedCases.length }}</span>
          <span class="text-xs text-muted font-medium">Completed</span>
        </router-link>
        <router-link
          :to="{ name: 'admin-applicants', query: { sort: 'deadline', dir: 'asc' } }"
          class="stat-cell flex-1 flex flex-col gap-1 py-5 px-7 no-underline text-inherit transition-colors duration-150 hover:bg-black/[0.025] max-[1100px]:basis-[40%] max-[1100px]:grow max-md:basis-[40%] max-md:grow max-md:py-4 max-md:px-4">
          <span class="text-[28px] font-bold leading-tight tracking-tight max-md:text-[22px]" :class="overdueCases.length > 0 ? 'text-[#e53935]' : 'text-ink'">{{ overdueCases.length }}</span>
          <span class="text-xs text-muted font-medium">Overdue</span>
        </router-link>
        <router-link
          :to="{ name: 'admin-applicants', query: { sort: 'deadline', dir: 'asc' } }"
          class="stat-cell flex-1 flex flex-col gap-1 py-5 px-7 no-underline text-inherit transition-colors duration-150 hover:bg-black/[0.025] max-[1100px]:basis-[40%] max-[1100px]:grow max-md:basis-[40%] max-md:grow max-md:py-4 max-md:px-4">
          <span class="text-[28px] font-bold leading-tight tracking-tight max-md:text-[22px]" :class="upcomingDeadlines.length > 0 ? 'text-[#f57c00]' : 'text-ink'">{{ upcomingDeadlines.length }}</span>
          <span class="text-xs text-muted font-medium">Due in 14 days</span>
        </router-link>
      </div>

      <div class="grid grid-cols-2 gap-5 max-[1100px]:grid-cols-1 max-md:grid-cols-1 max-md:gap-3">
        <!-- Deadlines needing attention -->
        <div class="bg-white border border-black/6 rounded-[20px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] max-md:p-4 max-md:rounded-[16px]">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Deadlines</span>
              <span v-if="urgentDeadlines.length" class="text-[11px] font-semibold px-[7px] py-px rounded-full bg-[#ffebee] text-[#e53935]">{{ urgentDeadlines.length }}</span>
            </div>
            <router-link :to="{ name: 'admin-deadlines' }" class="text-[12px] font-medium text-primary no-underline hover:opacity-80 hover:no-underline">View all</router-link>
          </div>
          <div v-if="urgentDeadlines.length === 0" class="text-[#2d7a0e] bg-[#f6fbf2] rounded-xl p-5 flex items-center justify-center gap-2 font-medium text-[13px]">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            All deadlines on track
          </div>
          <div v-else class="flex flex-col gap-0.5">
            <template v-if="overdueItems.length">
              <span class="deadline-section block text-[10px] font-bold uppercase tracking-[0.06em] text-[#e53935] px-2.5 pt-1.5 pb-0.5">Overdue</span>
              <router-link
                v-for="item in overdueItems.slice(0, 4)"
                :key="'o-' + item.applicant.id + item.deadline + (item.label || '')"
                :to="{ name: 'admin-applicant', params: { id: item.applicant.id } }"
                class="deadline-row flex items-center justify-between py-2 px-2.5 no-underline rounded-lg transition-colors duration-[120ms] bg-[#fef8f8] hover:bg-[#fef2f2] hover:no-underline">
                <div class="flex items-center gap-2.5 min-w-0">
                  <div class="w-[30px] h-[30px] rounded-full bg-[#eeedf5] text-primary flex items-center justify-center font-bold text-xs shrink-0">
                    {{ item.applicant.firstName ? item.applicant.firstName.charAt(0) : '?' }}
                  </div>
                  <div>
                    <span class="block text-[13px] font-semibold text-ink leading-snug">{{ item.applicant.firstName }} {{ item.applicant.lastName }}</span>
                    <span class="block text-xs text-muted">{{ item.label ? item.label + ' · ' : '' }}{{ item.deadline | formatDate }}</span>
                  </div>
                </div>
                <span class="text-[11px] font-semibold py-0.5 px-2 rounded-full whitespace-nowrap shrink-0 ml-2 bg-[#ffebee] text-[#e53935]">
                  {{ item.relative }}
                </span>
              </router-link>
            </template>
            <template v-if="upcomingItems.length">
              <span class="deadline-section block text-[10px] font-bold uppercase tracking-[0.06em] text-[#f57c00] px-2.5 pt-1.5 pb-0.5">Upcoming</span>
              <router-link
                v-for="item in upcomingItems.slice(0, 4)"
                :key="'u-' + item.applicant.id + item.deadline + (item.label || '')"
                :to="{ name: 'admin-applicant', params: { id: item.applicant.id } }"
                class="deadline-row flex items-center justify-between py-2 px-2.5 no-underline rounded-lg transition-colors duration-[120ms] hover:bg-black/[0.02] hover:no-underline">
                <div class="flex items-center gap-2.5 min-w-0">
                  <div class="w-[30px] h-[30px] rounded-full bg-[#eeedf5] text-primary flex items-center justify-center font-bold text-xs shrink-0">
                    {{ item.applicant.firstName ? item.applicant.firstName.charAt(0) : '?' }}
                  </div>
                  <div>
                    <span class="block text-[13px] font-semibold text-ink leading-snug">{{ item.applicant.firstName }} {{ item.applicant.lastName }}</span>
                    <span class="block text-xs text-muted">{{ item.label ? item.label + ' · ' : '' }}{{ item.deadline | formatDate }}</span>
                  </div>
                </div>
                <span class="text-[11px] font-semibold py-0.5 px-2 rounded-full whitespace-nowrap shrink-0 ml-2 bg-[#fff3e0] text-[#f57c00]">
                  {{ item.relative }}
                </span>
              </router-link>
            </template>
          </div>
        </div>

        <!-- Team workload -->
        <div class="bg-white border border-black/6 rounded-[20px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] max-md:p-4 max-md:rounded-[16px]">
          <div class="flex items-center gap-2 mb-4">
            <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Team workload</span>
            <span v-if="totalOpenTasks > 0" class="text-[11px] font-semibold px-[7px] py-px rounded-full bg-border text-primary">{{ totalOpenTasks }}</span>
          </div>
          <div v-if="managerWorkload.length === 0" class="text-muted text-[13px] text-center py-7 flex flex-col items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle></svg>
            No tasks currently assigned
          </div>
          <div v-else class="flex flex-col gap-3.5">
            <div v-for="manager in managerWorkload" :key="manager.name" class="flex items-center gap-3.5">
              <div class="flex items-center gap-2.5 min-w-[110px] max-md:min-w-[90px]">
                <img v-if="manager.photo" :src="manager.photo" class="w-[26px] h-[26px] rounded-full object-cover">
                <div v-else class="w-[26px] h-[26px] rounded-full bg-[#eeedf5] text-primary flex items-center justify-center font-bold text-[11px] shrink-0">{{ manager.name.charAt(0) }}</div>
                <span class="text-[13px] font-semibold text-ink">{{ manager.name }}</span>
              </div>
              <div class="flex-1 flex items-center gap-2.5">
                <div class="flex-1 h-2 bg-border rounded overflow-hidden">
                  <div class="h-full bg-primary rounded transition-[width] duration-300" :style="{ width: manager.percent + '%' }"></div>
                </div>
                <span class="text-xs text-muted font-semibold min-w-5 text-right">{{ manager.tasks }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Case progress -->
        <div class="bg-white border border-black/6 rounded-[20px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] max-md:p-4 max-md:rounded-[16px]">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Case progress</span>
              <span v-if="activeApplicants.length" class="text-[11px] font-semibold px-[7px] py-px rounded-full bg-border text-primary">{{ activeApplicants.length }}</span>
            </div>
            <router-link :to="{ name: 'admin-applicants' }" class="text-[12px] font-medium text-primary no-underline hover:opacity-80 hover:no-underline">View all</router-link>
          </div>
          <div v-if="activeApplicants.length === 0" class="text-muted text-[13px] text-center py-7 flex flex-col items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
            No cases in progress
          </div>
          <div v-else class="flex flex-col gap-0.5">
            <router-link
              v-for="applicant in sortedByProgress"
              :key="applicant.id"
              :to="{ name: 'admin-applicant', params: { id: applicant.id } }"
              class="flex items-center gap-3 py-[7px] px-2.5 no-underline rounded-lg transition-colors duration-[120ms] hover:bg-black/[0.02] hover:no-underline max-md:gap-2">
              <div class="flex items-center gap-2.5 min-w-[130px] shrink-0 max-md:min-w-[100px]">
                <div class="w-[26px] h-[26px] rounded-full bg-[#eeedf5] text-primary flex items-center justify-center font-bold text-[11px] shrink-0">{{ applicant.firstName ? applicant.firstName.charAt(0) : '?' }}</div>
                <div class="min-w-0">
                  <span class="block text-[13px] font-medium text-ink whitespace-nowrap overflow-hidden text-ellipsis leading-snug">{{ applicant.firstName }} {{ applicant.lastName }}</span>
                  <span v-if="applicant.deadline" class="block text-[11px] font-medium whitespace-nowrap" :class="deadlineColorClass(applicant)">{{ getDaysLeft(applicant) }}</span>
                </div>
              </div>
              <div class="flex-1 flex items-center gap-2.5">
                <div class="flex-1 h-1.5 bg-border rounded-[3px] overflow-hidden">
                  <div
                    class="h-full rounded-[3px] transition-[width] duration-300"
                    :class="getCompletedPercentage(applicant) === 100 ? 'bg-[#2d7a0e]' : 'bg-primary'"
                    :style="{ width: getCompletedPercentage(applicant) + '%' }">
                  </div>
                </div>
                <span class="text-xs font-semibold text-muted min-w-8 text-right">{{ getCompletedPercentage(applicant) }}%</span>
              </div>
            </router-link>
          </div>
        </div>

        <!-- Recent activity -->
        <div class="bg-white border border-black/6 rounded-[20px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] max-md:p-4 max-md:rounded-[16px]">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Recent activity</span>
              <span v-if="recentActivities.length" class="text-[11px] font-semibold px-[7px] py-px rounded-full bg-border text-primary">{{ recentActivities.length }}</span>
            </div>
            <router-link :to="{ name: 'admin-activities' }" class="text-[12px] font-medium text-primary no-underline hover:opacity-80 hover:no-underline">View all</router-link>
          </div>
          <div v-if="recentActivities.length === 0" class="text-muted text-[13px] text-center py-7 flex flex-col items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
            No recent activity
          </div>
          <div v-else class="flex flex-col">
            <router-link
              v-for="activity in recentActivities"
              :key="activity.id"
              :to="activity.applicantId ? { name: 'admin-applicant', params: { id: activity.applicantId } } : {}"
              class="group flex gap-2.5 py-2.5 border-t border-[#f0f0f5] first:border-t-0 no-underline hover:no-underline transition-colors duration-[120ms] rounded-md px-1 -mx-1 hover:bg-black/[0.025]">
              <img v-if="getPersonPhoto(activity.user)" :src="getPersonPhoto(activity.user)" class="shrink-0 w-[26px] h-[26px] rounded-full object-cover mt-0.5">
              <div v-else class="shrink-0 w-[26px] h-[26px] rounded-full bg-[#eeedf5] text-primary flex items-center justify-center font-bold text-[10px] mt-0.5">{{ activity.user ? activity.user.charAt(0) : '?' }}</div>
              <div class="flex-1 min-w-0">
                <div class="text-[13px] text-ink leading-snug">
                  <strong class="font-semibold mr-0.5">{{ activity.user }}</strong>
                  <span class="text-[#645B66]">{{ activity.message }}</span>
                </div>
                <span class="text-[11px] text-placeholder mt-0.5 inline-block">{{ formatTimeAgo(activity.createdAt) }}</span>
              </div>
            </router-link>
          </div>
        </div>

        <!-- Requirements breakdown -->
        <div class="bg-white border border-black/6 rounded-[20px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] max-md:p-4 max-md:rounded-[16px]">
          <div class="flex items-center gap-2 mb-4">
            <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Requirements</span>
            <span class="text-xs font-semibold text-placeholder">{{ totalRequirements }}</span>
          </div>
          <div v-if="totalRequirements === 0" class="text-muted text-[13px] text-center py-7 flex flex-col items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
            No requirements added yet
          </div>
          <template v-else>
            <div class="flex h-3.5 rounded-full overflow-hidden mb-4">
              <div
                v-for="status in activeStatuses"
                :key="status.key"
                class="h-full min-w-[3px] transition-[width] duration-300 cursor-default"
                :title="status.label + ': ' + status.count + ' (' + status.percent + '%)'"
                :style="{ width: status.percent + '%', background: segmentColor(status.key) }">
                <span v-if="status.percent >= 12" class="text-[10px] font-semibold text-white leading-[14px] text-center block">{{ status.percent }}%</span>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-x-4 gap-y-2.5 max-md:grid-cols-1 max-md:gap-2">
              <div v-for="status in requirementsByStatus" :key="status.key" class="flex items-center gap-2">
                <span class="w-[9px] h-[9px] rounded-full shrink-0" :style="{ background: segmentColor(status.key) }"></span>
                <span class="text-xs text-[#645B66] font-medium flex-1">{{ status.label }}</span>
                <span class="text-xs font-semibold text-ink">{{ status.count }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { PHOTOS } from '@/services/photos'
import { getCompletedPercentage } from '@/services/applicant-helpers'
import SkeletonOverview from '../UIComponents/SkeletonOverview'
dayjs.extend(relativeTime)

const STATUS_COLORS = {
  created: '#d6d8e3',
  collecting: '#d4a054',
  inProgress: '#3AA6DD',
  toCheck: '#7c5bea',
  complete: '#2d7a0e',
  sentToApplicant: '#1a8a6e'
}

export default {
  name: 'admin-overview',
  components: { SkeletonOverview },
  data () {
    return {
      isLoading: false
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      applicants: state => state.applicants.applicants,
      managers: state => state.managers.managers.filter(m => !m.hidden),
      allActivities: state => state.activities.allActivities
    }),
    greeting () {
      const hour = dayjs().hour()
      const name = this.user ? this.user.firstName : ''
      let timeOfDay = 'Good morning'
      if (hour >= 12 && hour < 18) timeOfDay = 'Good afternoon'
      else if (hour >= 18) timeOfDay = 'Good evening'
      return name ? timeOfDay + ', ' + name : timeOfDay
    },
    today () {
      return dayjs().format('dddd, MMMM D')
    },
    activeApplicants () {
      if (!this.applicants) return []
      return this.applicants.filter(a => a.isActive)
    },
    completedCases () {
      return this.activeApplicants.filter(a => this.getCompletedPercentage(a) === 100)
    },
    overdueCases () {
      const today = dayjs()
      return this.activeApplicants.filter(a => {
        if (this.getCompletedPercentage(a) === 100) return false
        return a.deadline && dayjs(a.deadline).isBefore(today, 'day')
      })
    },
    upcomingDeadlines () {
      const today = dayjs()
      const twoWeeks = dayjs().add(14, 'day')
      return this.activeApplicants.filter(a => {
        if (this.getCompletedPercentage(a) === 100) return false
        const d = dayjs(a.deadline)
        return a.deadline && (d.isAfter(today, 'day') || d.isSame(today, 'day')) && d.isBefore(twoWeeks, 'day')
      })
    },
    urgentDeadlines () {
      const today = dayjs()
      const twoWeeks = dayjs().add(14, 'day')
      const items = []

      this.activeApplicants.forEach(a => {
        if (this.getCompletedPercentage(a) === 100) return
        if (!a.deadline) return
        const d = dayjs(a.deadline)
        const isOverdue = d.isBefore(today, 'day')
        const isSoon = !isOverdue && d.isBefore(twoWeeks, 'day')

        if (isOverdue || isSoon) {
          const diff = d.diff(today, 'day')
          let relative
          if (diff < 0) relative = Math.abs(diff) + 'd overdue'
          else if (diff === 0) relative = 'Today'
          else if (diff === 1) relative = 'Tomorrow'
          else relative = diff + ' days left'

          items.push({
            applicant: a,
            deadline: a.deadline,
            isOverdue,
            relative,
            sortKey: d.valueOf()
          })
        }

        if (a.keyDates) {
          a.keyDates.forEach(kd => {
            if (kd.completed) return
            const kdd = dayjs(kd.date)
            const kdOverdue = kdd.isBefore(today, 'day')
            const kdSoon = !kdOverdue && kdd.isBefore(twoWeeks, 'day')
            if (kdOverdue || kdSoon) {
              const diff = kdd.diff(today, 'day')
              let relative
              if (diff < 0) relative = Math.abs(diff) + 'd overdue'
              else if (diff === 0) relative = 'Today'
              else if (diff === 1) relative = 'Tomorrow'
              else relative = diff + ' days left'

              items.push({
                applicant: a,
                deadline: kd.date,
                label: kd.label,
                isOverdue: kdOverdue,
                relative,
                sortKey: kdd.valueOf()
              })
            }
          })
        }
      })

      return items.sort((a, b) => a.sortKey - b.sortKey)
    },
    managerWorkload () {
      const counts = {}
      this.activeApplicants.forEach(a => {
        if (!a.requirements) return
        a.requirements.forEach(r => {
          if (!r.assignedTo) return
          if (r.status === 'complete' || r.status === 'sentToApplicant' || r.status === 'submitted') return
          r.assignedTo.forEach(m => {
            counts[m] = (counts[m] || 0) + 1
          })
        })
      })

      const maxTasks = Math.max(...Object.values(counts), 1)

      return Object.keys(counts)
        .map(key => ({
          name: key.charAt(0).toUpperCase() + key.slice(1),
          value: key,
          tasks: counts[key],
          percent: Math.round((counts[key] / maxTasks) * 100),
          photo: PHOTOS[key] || null
        })).sort((a, b) => b.tasks - a.tasks)
    },
    requirementsByStatus () {
      const statuses = {
        created: { label: 'Todo', count: 0 },
        collecting: { label: 'Data requested', count: 0 },
        inProgress: { label: 'In progress', count: 0 },
        toCheck: { label: 'To check', count: 0 },
        complete: { label: 'Ready', count: 0 },
        sentToApplicant: { label: 'Sent to applicant', count: 0 }
      }

      this.activeApplicants.forEach(a => {
        if (!a.requirements) return
        a.requirements.forEach(r => {
          const key = r.status || 'created'
          if (statuses[key]) {
            statuses[key].count++
          }
        })
      })

      const total = this.totalRequirements
      return Object.keys(statuses).map(key => ({
        key,
        label: statuses[key].label,
        count: statuses[key].count,
        percent: total > 0 ? Math.round((statuses[key].count / total) * 100) : 0
      }))
    },
    activeStatuses () {
      return this.requirementsByStatus.filter(s => s.count > 0)
    },
    totalRequirements () {
      let total = 0
      this.activeApplicants.forEach(a => {
        if (a.requirements) total += a.requirements.length
      })
      return total
    },
    totalOpenTasks () {
      let total = 0
      this.managerWorkload.forEach(m => { total += m.tasks })
      return total
    },
    sortedByProgress () {
      return [...this.activeApplicants].sort((a, b) => {
        return this.getCompletedPercentage(a) - this.getCompletedPercentage(b)
      })
    },
    overdueItems () {
      return this.urgentDeadlines.filter(item => item.isOverdue)
    },
    upcomingItems () {
      return this.urgentDeadlines.filter(item => !item.isOverdue)
    },
    recentActivities () {
      if (!this.allActivities) return []
      return this.allActivities.slice(0, 10)
    }
  },
  methods: {
    getCompletedPercentage (applicant) {
      return getCompletedPercentage(applicant)
    },
    getDaysLeft (applicant) {
      if (!applicant.deadline) return ''
      const diff = dayjs(applicant.deadline).diff(dayjs(), 'day')
      if (diff < 0) return Math.abs(diff) + 'd overdue'
      if (diff === 0) return 'Today'
      if (diff === 1) return 'Tomorrow'
      if (diff <= 14) return diff + 'd left'
      if (diff <= 60) return Math.ceil(diff / 7) + 'w left'
      return Math.ceil(diff / 30) + 'mo left'
    },
    getDeadlineClass (applicant) {
      if (!applicant.deadline) return ''
      const diff = dayjs(applicant.deadline).diff(dayjs(), 'day')
      if (diff < 0) return 'admin-overview__progress-due--red'
      if (diff <= 14) return 'admin-overview__progress-due--orange'
      return ''
    },
    deadlineColorClass (applicant) {
      if (!applicant.deadline) return 'text-muted'
      const diff = dayjs(applicant.deadline).diff(dayjs(), 'day')
      if (diff < 0) return 'text-[#e53935]'
      if (diff <= 14) return 'text-[#f57c00]'
      return 'text-muted'
    },
    segmentColor (key) {
      return STATUS_COLORS[key] || '#d6d8e3'
    },
    formatTimeAgo (date) {
      if (!date) return ''
      return dayjs(date).fromNow()
    },
    getPersonPhoto (name) {
      if (!name) return null
      return PHOTOS[name.toLowerCase()] || null
    }
  },
  created () {
    if (this.user && !this.user.isAdmin) {
      this.$router.push({ name: 'admin-applicants' })
      return
    }
    this.isLoading = true
    this.$store.dispatch('GET_ALL_ACTIVITIES').catch(() => {})
    Promise.all([
      this.$store.dispatch('GET_APPLICANTS'),
      this.$store.dispatch('GET_BODIES').catch(() => {})
    ]).then(() => {
      this.isLoading = false
    }).catch(() => {
      this.isLoading = false
    })
  }
}
</script>

<style scoped>
/* Adjacent sibling: deadline section after a deadline row gets a top border separator */
.deadline-row + .deadline-section {
  margin-top: 10px;
  border-top: 1px solid #e2e4ed;
  padding-top: 10px;
}

/* Stat cells: right border between siblings, removed on last child */
.stat-cell {
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  transition: background-color 0.15s;
}

.stat-cell:first-child {
  border-radius: 20px 0 0 20px;
}

.stat-cell:last-child {
  border-right: none;
  border-radius: 0 20px 20px 0;
}

/* At 1100px and 768px the second stat loses its right border */
@media (max-width: 1100px) {
  .stat-cell:nth-child(2) {
    border-right: none;
  }
}

@media (max-width: 768px) {
  .stat-cell:nth-child(2) {
    border-right: none;
  }
}
</style>
